// Yin Yoga Frome - Natural yoga-specific content
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, Users, MapPin } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { usePageMeta } from "../../components/shared/SEOHelper";

const YinYogaFrome = () => {
  usePageMeta({
    title: "Yin Yoga Frome | Deep Stretch & Release | Annelie Lily",
    description: "Yin yoga classes in Frome, Somerset. Slow, meditative practice with deep stretches held for several minutes. Release tension and find stillness.",
    canonical: "https://annelielily.com/yoga-classes/yin-yoga"
  });

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="Yin Yoga" currentSlug="yoga-classes/yin-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
                Yin Yoga in Frome
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
                Yin Yoga Classes
              </h1>
              <p className="text-neutral-700 leading-relaxed mb-6">
                Slower, quieter, deeper. We hold poses for 3-5 minutes while the body gradually 
                releases tension you didn't know you were carrying. If you struggle to switch off, 
                this might be exactly what you need.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary">
                  Book a Class <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-neutral-600">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold" />
                  60-75 minutes
                </span>
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gold" />
                  Small groups
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  Frome, Somerset
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/2mu1bh0v_IMG_2004.jpg"
                  alt="Yin yoga class with students in supported forward fold using bolsters, practicing deep release in a peaceful studio"
                  className="w-full h-full object-contain bg-muted"
                  width={800}
                  height={800}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* What Makes Yin Different */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Why Yin is Different
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Most yoga is about movement. Yin is about stillness. Instead of flowing between poses 
                like in <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link>, 
                we find a position, make it comfortable with bolsters and blocks, and then stay there 
                — three minutes, sometimes five.
              </p>
              <p>
                The long holds target connective tissue rather than muscles. Fascia, ligaments, the deep 
                stuff around the joints that ordinary stretching doesn't reach. The release comes gradually, 
                like ice slowly melting.
              </p>
              <p>
                It's also a mental practice. Sitting with discomfort — even mild discomfort — teaches you 
                something. That you can tolerate more than you think. That not everything needs to be fixed 
                immediately. That sometimes the best response to difficulty is simply to breathe.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Who Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Who Benefits From Yin?
            </h2>
            <div className="bg-white/70 p-6 md:p-8 rounded-xl border border-gold/10">
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>People who can't switch off</strong> — busy minds, racing thoughts, that feeling of always being 'on'. Yin creates space.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Anyone with tight hips</strong> — desk workers, drivers, runners. The hips hold so much tension, and Yin is brilliant for releasing it.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Athletes needing recovery</strong> — a different kind of training day. Many runners and cyclists use Yin to maintain flexibility.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>People who find <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha</Link> too fast</strong> — if you want the benefits of yoga without the flow, Yin is a gentler entry point.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Anyone dealing with stress or anxiety</strong> — the stillness and breathwork have a direct effect on the nervous system.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What to Expect in Class
            </h2>
            <div className="space-y-4 text-neutral-700">
              <p>
                We'll do maybe 6-8 poses in an hour-long class, far fewer than Hatha. Each one is held 
                for 3-5 minutes, supported by props so you're not straining to hold yourself up. The 
                aim is passive — you find the edge of the stretch and then let go.
              </p>
              <p>
                <strong>It's not always comfortable.</strong> Holding still for that long can feel 
                challenging, especially in hip openers. But the sensation should be mild, a gentle pull 
                rather than sharp pain. I'll guide you through it, and you can always come out of a pose 
                if you need to.
              </p>
              <p>
                <strong>Bring warm clothes.</strong> Body temperature drops when you're not moving. A 
                blanket to cover yourself is useful, especially in winter. I keep the room warm, but 
                layers help.
              </p>
              <p>
                <strong>Afterwards?</strong> People often describe feeling like they've had a massage. 
                Softer, looser, calmer. Some people feel quite tired — in a good way, like after a 
                very good night's sleep.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Questions */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Common Questions
            </h2>
            <div className="space-y-4">
              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">I'm not flexible at all — is this okay?</h3>
                <p className="text-neutral-600 text-sm">
                  Yin isn't about achieving impressive shapes. We use props to bring the floor up to you, 
                  so you can work at your own level. Actually, people who are naturally stiff often benefit 
                  most because they have the most to release.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">Why hold poses for so long?</h3>
                <p className="text-neutral-600 text-sm">
                  Connective tissue responds differently than muscle. It needs sustained, gentle pressure 
                  over time to release. Think of bending a plastic ruler slowly versus quickly — slow and 
                  steady creates lasting change without damage.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">Can I do both Yin and Hatha?</h3>
                <p className="text-neutral-600 text-sm">
                  Yes — they complement each other beautifully. <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> for strength and active flexibility, 
                  Yin for deep release and stillness. Many people end up doing one of each per week.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">Is Yin suitable if I have an injury?</h3>
                <p className="text-neutral-600 text-sm">
                  Often, yes — but let me know what's going on before class. Some poses need modifying, 
                  and occasionally we might skip certain positions entirely. The slow pace means we can 
                  be careful and considered.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
                Ready to Slow Down?
              </h2>
              <p className="text-neutral-600 mb-6">
                If constant busyness is wearing you out and you need something that actually helps 
                you let go, Yin might be worth trying. Drop me a message and we can chat about whether 
                it's right for you.
              </p>
              <Link to="/contact" className="btn-primary">
                Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/70 p-6 rounded-xl border border-gold/10"
            >
              <h3 className="font-heading text-lg text-neutral-800 mb-4">Also Available</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/yoga-classes/hatha-yoga" className="text-neutral-600 hover:text-gold transition-colors flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" /> Hatha Yoga — more movement, active practice
                  </Link>
                </li>
                <li>
                  <Link to="/yoga-classes/private-yoga" className="text-neutral-600 hover:text-gold transition-colors flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" /> Private sessions — 1-to-1 attention
                  </Link>
                </li>
                <li>
                  <Link to="/treatments" className="text-neutral-600 hover:text-gold transition-colors flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" /> Treatments — massage, Reiki & more
                  </Link>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Areas - Yin Yoga */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">
            Yin yoga for your area:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Yin Yoga Bath", link: "/areas/yoga-bath/yin-yoga" },
              { name: "Yin Yoga Trowbridge", link: "/areas/yoga-trowbridge/yin-yoga" },
              { name: "Yin Yoga Warminster", link: "/areas/yoga-warminster/yin-yoga" },
              { name: "Yin Yoga Wells", link: "/areas/yoga-wells/yin-yoga" },
              { name: "Yin Yoga Radstock", link: "/areas/yoga-radstock/yin-yoga" },
              { name: "Yin Yoga Westbury", link: "/areas/yoga-westbury/yin-yoga" },
              { name: "Yin Yoga Shepton Mallet", link: "/areas/yoga-shepton-mallet/yin-yoga" },
            ].map((area) => (
              <Link
                key={area.name}
                to={area.link}
                className="text-sm text-gold hover:underline underline-offset-4"
              >
                {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YinYogaFrome;
